import { Plugin } from "prosemirror-state";
import type { EditorState, Transaction } from "prosemirror-state";
import type { Node as PmNode } from "prosemirror-model";
import { Mapping } from "prosemirror-transform";
import { schema } from "./schema";
import { truncateUrl } from "./link";

// Bare URLs in card text. Deliberately http(s)-only (no bare "www.") to avoid
// false positives, and excludes whitespace/brackets so it stops at word bounds.
const URL_RE = /\bhttps?:\/\/[^\s<>()]+/g;

// Trailing punctuation that is almost never part of the URL (sentence enders,
// a dangling close-paren from "(see http://x)").
const TRAILING = /[.,;:!?)\]]+$/;

// Index of the top-level block (doc child) containing `pos`.
function topBlockIndex(state: EditorState, pos: number): number {
  return state.doc.resolve(pos).index(0);
}

// Scan one already-settled top-level block for bare URLs and wrap each in a
// link mark, with the visible text truncated. Mutates `tr`; returns whether it
// made any change.
function linkifyBlock(tr: Transaction, blockStart: number, block: PmNode): boolean {
  const link = schema.marks["link"]!;
  type Repl = { from: number; to: number; url: string };
  const repls: Repl[] = [];

  block.descendants((node: PmNode, offset: number) => {
    if (!node.isText || !node.text) return;
    // Skip code (no marks allowed) and anything already linked.
    if (link.isInSet(node.marks)) return;
    if (!node.marks.every((m) => m.type !== schema.marks["code"])) return;
    const base = blockStart + 1 + offset; // +1: into the block's content
    for (const m of node.text.matchAll(URL_RE)) {
      let url = m[0];
      const trail = TRAILING.exec(url);
      if (trail) url = url.slice(0, url.length - trail[0].length);
      if (!url) continue;
      const from = base + m.index;
      repls.push({ from, to: from + url.length, url });
    }
  });
  if (repls.length === 0) return false;

  // Apply right-to-left so earlier positions stay valid.
  repls.sort((a, b) => b.from - a.from);
  for (const r of repls) {
    const text = truncateUrl(r.url);
    tr.replaceRangeWith(r.from, r.to, schema.text(text, [link.create({ href: r.url })]));
  }
  return true;
}

// Auto-link plugin: when the cursor leaves the top-level block it was in,
// linkify that block. We act on block *exit* (not per keystroke) so a URL is
// only converted once the user is done with the line.
export function linkAutodetectPlugin(): Plugin {
  return new Plugin({
    appendTransaction(trs, oldState, newState) {
      if (!trs.some((t) => t.docChanged) && oldState.selection.eq(newState.selection)) {
        return null;
      }
      const oldIdx = topBlockIndex(oldState, oldState.selection.from);
      const newIdx = topBlockIndex(newState, newState.selection.from);

      // Combined mapping across all transactions, to carry the old block's
      // position into the new document.
      const mapping = new Mapping();
      for (const t of trs) mapping.appendMapping(t.mapping);

      const oldBefore = oldState.doc.resolve(oldState.selection.from).before(1);
      const mappedStart = mapping.map(oldBefore);
      const block = newState.doc.childAfter(mappedStart).node;
      // Only when the cursor actually moved to a different block, and the old
      // block still exists as a textblock-bearing node.
      if (oldIdx === newIdx || !block || !block.isBlock) return null;

      const tr = newState.tr;
      if (!linkifyBlock(tr, mappedStart, block)) return null;
      return tr;
    },
  });
}
